import { from } from "./rpc";
import { translateError } from "./errors";

/** Mesmo formato de @vixio/ui (TableSource), repetido aqui para não criar dependência circular. */
export interface SourceQuery {
  page: number;
  pageSize: number;
  sort: { key: string; dir: "asc" | "desc" } | null;
  search: string;
  filters: Record<string, string>;
}

// Construtor de consulta do PostgREST (supabase-js), sem amarrar aos tipos genéricos dele.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type QB = any;

export interface PostgrestSourceOptions {
  schema: string;
  table: string;
  select?: string;
  /** Filtra por org_id (quase toda tabela de módulo tem). */
  orgId?: string | null;
  /** Colunas buscadas com ilike pelo texto da barra de busca. */
  searchColumns?: string[];
  /** Filtro de cada chave da barra de filtros; padrão: eq(chave, valor). */
  filters?: Record<string, (qb: QB, value: string) => QB>;
  /** Filtros fixos da tela. */
  base?: (qb: QB) => QB;
  /** Ordenação de desempate (estável entre páginas). */
  tiebreak?: string;
  /** Parte extra da chave de cache (ex.: filtros fixos). */
  keyExtra?: unknown[];
}

function cleanSearch(s: string): string {
  return s.replace(/[,()"*\\%]/g, " ").trim();
}

/**
 * Fonte paginada no servidor para o DataTable: ordenação (`order`), filtro (`eq`/custom),
 * busca (`or=(col.ilike.*x*)`) e total por `Content-Range` (`count=exact`).
 */
export function postgrestSource<Row>(o: PostgrestSourceOptions) {
  return {
    key: [o.schema, o.table, o.orgId ?? null, o.select ?? "*", ...(o.keyExtra ?? [])] as const,
    async fetch(q: SourceQuery): Promise<{ rows: Row[]; total: number | null }> {
      let b: QB = from(o.schema, o.table).select(o.select ?? "*", { count: "exact" });
      if (o.orgId) b = b.eq("org_id", o.orgId);
      if (o.base) b = o.base(b);
      const s = cleanSearch(q.search);
      if (s && o.searchColumns?.length) {
        b = b.or(o.searchColumns.map((c) => `${c}.ilike."*${s}*"`).join(","));
      }
      for (const [k, v] of Object.entries(q.filters)) {
        if (v === "" || v === undefined) continue;
        const f = o.filters?.[k];
        b = f ? f(b, v) : b.eq(k, v);
      }
      if (q.sort) b = b.order(q.sort.key, { ascending: q.sort.dir === "asc", nullsFirst: false });
      if (o.tiebreak) b = b.order(o.tiebreak, { ascending: true });
      const fromRow = q.page * q.pageSize;
      b = b.range(fromRow, fromRow + q.pageSize - 1);
      const r = await b;
      if (r.error) throw translateError(r.error);
      return { rows: (r.data ?? []) as Row[], total: typeof r.count === "number" ? r.count : null };
    },
  };
}
